import { Halfedge } from "../core/Halfedge";
import { HalfedgeDS } from "../core/HalfedgeDS";
import { Vertex } from "../core/Vertex";

/**
 * Adds a triangular face for (a,b,c). Ensures three directed halfedges
 * (a->b, b->c, c->a) exist and are FREE, then calls addFace.
 *
 * If an undirected edge already exists but its outgoing halfedge is not free,
 * we create a parallel edge (allowParallels=true) and take the free one.
 */
export function createFace(struct: HalfedgeDS, a: Vertex, b: Vertex, c: Vertex) {
  if (a === b || b === c || c === a) return false;
  if (area2(a, b, c) <= 1e-12) return false;

  // A failed attempt must not leave the edges it eagerly created behind —
  // phantom free edges make vertices read as boundary forever.
  const attempt = (v1: Vertex, v2: Vertex, v3: Vertex): boolean => {
    const created: Halfedge[] = [];
    try {
      const h12 = ensureFreeOutHalfedge(struct, v1, v2, created);
      const h23 = ensureFreeOutHalfedge(struct, v2, v3, created);
      const h31 = ensureFreeOutHalfedge(struct, v3, v1, created);
      // addFace can silently return undefined (halfedge already owned) —
      // treat anything falsy as a failure.
      if (struct.addFace([h12, h23, h31])) return true;
    } catch {
      // fall through to cleanup
    }
    for (const he of created) {
      if (he.face === null && he.twin.face === null) {
        try {
          struct.removeEdge(he, false);
        } catch {
          // leave it — better a stray edge than a throw from cleanup
        }
      }
    }
    return false;
  };

  return attempt(a, b, c) || attempt(a, c, b); // preferred, then reversed winding
}


export function area2(a: Vertex, b: Vertex, c: Vertex): number {
  const abx = b.position.x - a.position.x;
  const aby = b.position.y - a.position.y;
  const abz = b.position.z - a.position.z;
  const acx = c.position.x - a.position.x;
  const acy = c.position.y - a.position.y;
  const acz = c.position.z - a.position.z;
  return Math.hypot(
    aby * acz - abz * acy,
    abz * acx - abx * acz,
    abx * acy - aby * acx,
  );
}

/**
 * Returns a FREE halfedge going out of `from` to `to`.
 * If a->b exists and is free, use it. If it exists but is not free, create
 * a parallel edge and take the free one. If it does not exist, create it.
 */
export function ensureFreeOutHalfedge(
  struct: HalfedgeDS,
  from: Vertex,
  to: Vertex,
  createdOut?: Halfedge[],
): Halfedge {
  let he = from.getHalfedgeToVertex(to);

  if (he) {
    if (he.face === null) return he; // already free, use it
    // Occupied on this side: creating a parallel edge here would leave a
    // permanently half-free duplicate (an unclosable 2-cycle) — in a
    // manifold triangle mesh this situation always means the caller's
    // bookkeeping diverged, so fail the attempt instead.
    throw new Error(`Halfedge ${from.id} -> ${to.id} is occupied`);
  } else {
    he = struct.addEdge(from, to, /*allowParallels=*/ false);
    createdOut?.push(he);
    if (he.face === null) return he;
  }

  // In theory we should never get here, but just in case:
  // try to find any outgoing free halfedge to 'to' after creation
  const retry = from.getHalfedgeToVertex(to);
  if (retry && retry.face === null) return retry;

  throw new Error(`No free halfedge available for ${from.id} -> ${to.id}`);
}